import * as Yup from 'yup';
import {
  endOfToday,
  isAfter,
  differenceInYears,
  parse,
  isValid,
  format,
  differenceInCalendarDays,
} from 'date-fns';

type YupSchema = Yup.StringSchema<
  string | null | undefined,
  Yup.AnyObject,
  undefined,
  ''
>;
const DATE_FMT = 'dd.MM.yyyy';

function parseStrict(value: string) {
  // Parse and ensure the formatted result matches exactly (strict format)
  const d = parse(value, DATE_FMT, new Date());
  if (!isValid(d)) return null;
  // Ensures "31/02/2025" (etc.) fails: reformat must equal original
  if (format(d, DATE_FMT) !== value) return null;
  return d;
}

const validateInvalidDate = (v: string | null | undefined) => {
  if (!v) return true;
  const d = parseStrict(v);
  if (!d) return false;
  return true;
};

const validateAgeGte14 = (v: string | null | undefined) => {
  if (!v) return true;
  const d = parseStrict(v);
  if (!d) return false;
  return differenceInYears(new Date(), d) >= 14;
};

const validateAgeLte100 = (v: string | null | undefined) => {
  if (!v) return true;
  const d = parseStrict(v);
  if (!d) return false;
  return differenceInYears(new Date(), d) <= 100;
};

const validateDateNotInFuture = (v: string | null | undefined) => {
  if (!v) return true;
  const d = parseStrict(v);
  if (!d) return false;
  return !isAfter(d, endOfToday()); // allow today, reject > today
};

const validateIssueDateInRange = (v: string | null | undefined) => {
  if (!v) return true;
  const d = parseStrict(v);
  if (!d) return false;
  return (
    differenceInYears(new Date(), d) <= 100 &&
    differenceInCalendarDays(new Date(), d) >= 1
  );
};

export const InputDateValidationConfig: Record<
  string,
  ((schema: YupSchema) => YupSchema)[]
> = {
  pass_issued_at: [
    (schema: YupSchema): YupSchema =>
      schema.test(
        'not-in-format',
        'error_validation_receiver_details_enter_valid_date',
        validateInvalidDate
      ),
    (schema: YupSchema): YupSchema =>
      schema.test(
        'not-in-future',
        'error_validation_receiver_details_issue_not_future_date',
        validateDateNotInFuture
      ),
    (schema: YupSchema): YupSchema =>
      schema.test(
        'not-in-range',
        'error_validation_receiver_details_out_of_range',
        validateIssueDateInRange
      ),
  ],
  birthday_at: [
    (schema: YupSchema): YupSchema =>
      schema.test(
        'not-in-format',
        'error_validation_receiver_details_enter_valid_date',
        validateInvalidDate
      ),
    (schema: YupSchema): YupSchema =>
      schema.test(
        'not-in-future',
        'error_validation_receiver_details_not_future_date',
        validateDateNotInFuture
      ),
    (schema: YupSchema): YupSchema =>
      schema.test('age-gte-14', 'min_age_date', validateAgeGte14),
    (schema: YupSchema): YupSchema =>
      schema.test('age-lte-100', 'max_age_date', validateAgeLte100),
  ],
};
